import { useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { UseFormReturn } from 'react-hook-form';
import { BackButton } from '@/components/common/BackButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { SurveyForm, type SurveyFormHandle } from '../components/SurveyForm/SurveyForm';
import { useCreateSurvey } from '../hooks/useCreateSurvey';
import type { CreatedSurveyRef, CreateSurveyFormValues } from '../types/survey.types';
import { toCreateSurveyPayload } from '../utils/surveyFormMappers';

export function SurveyCreatePage() {
  const router = useRouter();
  const formRef = useRef<SurveyFormHandle>(null);
  const submittedValuesRef = useRef<CreateSurveyFormValues | null>(null);

  function goToList() {
    void router.navigate({ to: '/feedback/surveys' });
  }

  function goToDetail(survey: CreatedSurveyRef) {
    if (submittedValuesRef.current) {
      formRef.current?.reset(submittedValuesRef.current);
      submittedValuesRef.current = null;
    }
    void router.navigate({
      to: '/feedback/surveys/$surveyId',
      params: { surveyId: survey.id },
    });
  }

  const { mutate: createSurveyMutation, isPending } = useCreateSurvey(goToDetail);

  function handleSubmit(
    values: CreateSurveyFormValues,
    form: UseFormReturn<CreateSurveyFormValues>,
  ) {
    submittedValuesRef.current = form.getValues();
    createSurveyMutation(toCreateSurveyPayload(values));
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <PageHeader title="Create Survey" leading={<BackButton onClick={goToList} />} />

      <SurveyForm
        ref={formRef}
        onSubmit={handleSubmit}
        onCancel={goToList}
        submitLabel="Create Survey"
        pendingLabel="Creating..."
        isPending={isPending}
      />
    </div>
  );
}
